// token.interceptor.ts
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { SharedService } from '../shared/services/shared.service';
import { environment } from '../../environments/environment';

export const tokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const sharedService = inject(SharedService);

  // Skip the interceptor for 'assets/' requests
  if (req.url.includes('assets/')) {
    return next(req);
  }

  // Check if the request should bypass the interceptor
  if (req.headers.has('skip-interceptor')) {
    const clonedRequest = req.clone({
      headers: req.headers.delete('skip-interceptor'),
    });
    return next(clonedRequest).pipe(
      catchError((error: unknown) => throwError(() => error))
    );
  }

  // Skip external buckets — they don't need cookies or auth
  if (
    req.url.includes('https://s3.ap-southeast-2.amazonaws.com') ||
    req.url.includes('https://storage.googleapis.com')
  ) {
    return next(req);
  }

  const embedToken = sharedService.verificationToken();
  const isApiUrl =
    req.url.startsWith(environment.apiUrl) ||
    req.url.startsWith(environment.courseApiUrl) ||
    req.url.startsWith(environment.authApiUrl);

  let clonedRequest = req;

  if (embedToken) {
    // Embed verification flow — keep header-based auth
    clonedRequest = req.clone({
      setHeaders: { verification: `Bearer ${embedToken}` },
    });
  } else if (isApiUrl) {
    // Localhost dev: cookies aren't set cross-origin, so fall back to
    // the access_token from localStorage and attach it as a Bearer header.
    const isLocalhost =
      typeof window !== 'undefined' && window.location.hostname === 'localhost';
    const localToken = isLocalhost
      ? (typeof localStorage !== 'undefined' ? localStorage.getItem('access_token') : null)
      : null;

    if (localToken) {
      clonedRequest = req.clone({
        setHeaders: { Authorization: `Bearer ${localToken}` },
        withCredentials: true,
      });
    } else {
      // Cookie-based auth — send credentials with API calls
      clonedRequest = req.clone({ withCredentials: true });
    }
  }

  return next(clonedRequest).pipe(
    catchError((error: unknown) => throwError(() => error))
  );
};
